import React from 'react';
import { Frame } from "scenejs";
import { CAROUSEL_CARD, IMAGE, CONTENT_CARD, CONTAINER } from '../../constants/widgetTypes';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';

const PARENTS_DISALLOWING_IMAGE_TRANSFORMATIONS = [CONTENT_CARD];
export const HIGH_PRIORITY_RESOURCE_THRESHOLD = 800;
export const SPINNER_CLASS = 'widget-triggers-loading';

export function getStylesForLayout(layout, breakpoint, container, parentContainer, widgetContext) {
  var widgetStyle = new Frame(layout).toCSSObject();
  if (container) {
    const minWidth = get(container, `properties.${breakpoint}.container.minWidth`);
    const alignment = get(container, `properties.${breakpoint}.container.alignment.position`) || get(container, `properties.${breakpoint}.container.alignment`, 'center');

    var multiplier = 0.5;
    switch (alignment) {
      case 'left':
        multiplier = 0;
        break;

      case 'right':
        multiplier = 1;
        break;

      case 'center':
      default:
        multiplier = 0.5;
        break;
    }
            
    widgetStyle = {
      ...widgetStyle,
      marginLeft: !minWidth ? '0px' : `calc((100% - ${minWidth}px) * ${multiplier})`
    }

    if (container?.type === CAROUSEL_CARD) {
      const parentWidget = parentContainer;
      const isFreeSize = get(parentWidget, `properties.desktop.free`, false);

      if (isFreeSize) {

        widgetStyle = {
          ...widgetStyle,
          marginLeft: '0px'
        }
      }
    }

    if (container?.type === CONTAINER) {

        widgetStyle = {
          ...widgetStyle,
          marginLeft: '0px'
        }
    }
  }

  return widgetStyle;
}

export function colorToString(color) {
  if (!color) {
    return "inherit";
  }

  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;;
}

// Replace if aktiv-grotesk font is found
export function injectAktivFallback(content) { 
  if (!content || typeof content !== "string") {
    return content;
  }
  return content.replace(/\baktiv-grotesk\b/g, 'aktiv-grotesk, aktivGrotesk');
}

export const isActionTriggerLoading = (action) => {
  if (!action) return false;
  switch (action.type) {
    case 'link':
    case 'page':
      return !action.newTab;
    default:
      return false;
  }
}

export const getChildWidgetProps = (childWidgetProps, parentType) => {
  const shouldDisableTransformations = childWidgetProps.type === IMAGE && PARENTS_DISALLOWING_IMAGE_TRANSFORMATIONS.includes(parentType);
  
  if (shouldDisableTransformations) {
    const widgetProps = cloneDeep(childWidgetProps);
    // Disable image transformations to prevent redundant network requests for the same resource.
    widgetProps.noTransformations = true;
    return widgetProps;
  }

  return childWidgetProps;
}

function getY(layout){
  if (!layout) {
    return 0;
  }

  const frame = new Frame(layout);
  const translate = frame.get("transform", "translate") || '0px, 0px';
  const position = translate.split(",").map(str => {
    str = str.trim();
    return parseFloat(str);
  });

  return position[1];
}

export const getYForWidget = (widget, widgetsById, breakpoint = 'desktop') => {
  if (!widget.parent) {
    return getY(widget?.properties?.[breakpoint]?.layout);
  }

  let currentY = getY(widget?.properties?.[breakpoint]?.layout);
  
  const parentWidget = widgetsById[widget.parent];

  if (parentWidget) {
    const parentTotalY = getYForWidget(parentWidget, widgetsById, breakpoint) || 0;
    
    return currentY + parentTotalY;
  }

  return currentY;
}

/** Collects ancestor widgets by traversing the parent chain. */
export function getAllAncestors(widget, widgets, excludeWidgetTypes = []) {
  if (!widget || !widget.parent || !widgets) {
    return [];
  }

  const ancestors = [];
  let currentWidget = widget;

  while (currentWidget && currentWidget.parent) {
    const parentWidget = widgets.find(w => w.uid === currentWidget.parent);
    if (!parentWidget) break;

    if (excludeWidgetTypes.includes(get(parentWidget, 'type'))) {
      currentWidget = get(parentWidget, 'parent');
      continue;
    }
    
    ancestors.push(parentWidget);
    currentWidget = parentWidget;
  }

  return ancestors;
}

export function getBoundingRectFromLayout(layout) {
  if (!layout) {
    return null;
  }

  const frame = new Frame(layout);
  const translate = frame.get("transform", "translate") || '0px, 0px';
  const position = translate.split(",").map(str => {
    str = str.trim();
    return parseFloat(str);
  });

  const width = parseFloat(frame.get('width'));
  const height = parseFloat(frame.get('height'));

  return {
    x: position[0],
    y: position[1],
    width: width,
    height: height
  }
}

/** Applies height adjustments to ancestor widgets when no overflow occurs. */
export function applyHeightChangesToAncestors(ancestors, autoGrowWidget, breakpoint, delta) {
  const changes = {};

  const autoGrowLayout = get(autoGrowWidget, `properties.${breakpoint}.layout`);
  const autoGrowRect = getBoundingRectFromLayout(autoGrowLayout);
  const autoGrowBottom = autoGrowRect.y + autoGrowRect.height;

  ancestors.forEach(item => {
    const layout = get(item, `properties.${breakpoint}.layout`);
    const rect = getBoundingRectFromLayout(layout);

    if (autoGrowBottom > rect.y + rect.height) return;

    const frame = new Frame(layout);
    if (frame.has('height')) {
      frame.set('height', `${rect.height + delta}px`);
    }

    changes[item.uid] = {
      id: item.uid,
      path: `properties.${breakpoint}.layout`,
      value: frame.get()
    };
  });

  return changes;
}